"use client"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useCallback, useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { KpiCard, KpiCardSkeleton } from "@/components/ui/analytics/kpi-card"
import { formatCurrency } from "@/components/shopify/attribution-dashboard/utils"
import { ProductMultiSelect } from "./product-multi-select"
import { Package, ShoppingCart, TrendingUp, Users, Search, Info, Plus, X } from "lucide-react"
import {
  Tooltip as RadixTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from "recharts"
import { OrdersLink } from "./orders-link"
import type { BundleAnalysis } from "./types"
import type { ProductOption } from "@/types/shopify"
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"




const CHART_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444"]
const MIN_SLOTS = 2
const MAX_SLOTS = 4

interface BundleBuilderViewProps {
  integrationId: string
  monthStartUtc: string
  monthEndUtc: string
  minimumOrderTotal: number
  /** Each slot is a group of products treated as one logical product */
  slots: ProductOption[][]
  onSlotsChange: (slots: ProductOption[][]) => void
  /** Display label per slot (saved-set name or null). Same length as slots. */
  slotLabels: (string | null)[]
  onSlotLabelsChange: (labels: (string | null)[]) => void
  bundle: BundleAnalysis | null
  isLoading: boolean
  error?: string | null
}

export function BundleBuilderView({
  integrationId,
  monthStartUtc,
  monthEndUtc,
  minimumOrderTotal,
  slots,
  onSlotsChange,
  slotLabels,
  onSlotLabelsChange,
  bundle,
  isLoading,
  error,
}: BundleBuilderViewProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()


  const tUi = useTranslations("Shopify.ShopifyCrossSellBundleBuilderView")

  const filledSlotCount = slots.filter((s) => s.length > 0).length

  const handleSlotChange = useCallback(
    (index: number, products: ProductOption[]) => {
      const next = [...slots]
      next[index] = products
      onSlotsChange(next)
    },
    [slots, onSlotsChange],
  )

  const handleAddSlot = useCallback(() => {
    if (slots.length >= MAX_SLOTS) return
    onSlotsChange([...slots, []])
    onSlotLabelsChange([...slotLabels, null])
  }, [slots, onSlotsChange, slotLabels, onSlotLabelsChange])

  const handleRemoveSlot = useCallback(
    (index: number) => {
      if (slots.length <= MIN_SLOTS) return
      onSlotsChange(slots.filter((_, i) => i !== index))
      onSlotLabelsChange(slotLabels.filter((_, i) => i !== index))
    },
    [slots, onSlotsChange, slotLabels, onSlotLabelsChange],
  )

  const handleSlotLabelChange = useCallback(
    (index: number, name: string | null) => {
      const next = [...slotLabels]
      next[index] = name
      onSlotLabelsChange(next)
    },
    [slotLabels, onSlotLabelsChange],
  )

  const chartData = useMemo(() => {
    if (!bundle) return []

    return bundle.group_stats.map((stat, idx) => ({
      name: slotLabels[idx] ?? stat.title ?? `Slot ${idx + 1}`,
      in_bundle: bundle.full_bundle_orders,
      without_bundle: stat.total_orders - bundle.full_bundle_orders,
      color: CHART_COLORS[idx % CHART_COLORS.length],
    }))
  }, [bundle, slotLabels])

  return (
    <div className="space-y-4">
      {/* Slot-based product groups */}
      <div>
        <label className="text-sm font-medium mb-1 block">
          <span key={tUi("select24ProductGroupsForBundleAnalysis")}>{tUi("select24ProductGroupsForBundleAnalysis")}</span></label>
        <p className="text-xs text-muted-foreground mb-3">
          <span key={tUi("eachSlotCanContainAProductSetE")}>{tUi("eachSlotCanContainAProductSetE")}</span></p>

        <div className="space-y-3 max-w-lg">
          {slots.map((slot, idx) => (
            <div key={idx} className="relative">
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                />
                <span className="text-xs font-medium text-muted-foreground"><TranslationText>{tUi.rich("slotValue0Value1", { value0: String(idx + 1), value1: String(slot.length > 1 && ` (${slot.length} products)`) })}</TranslationText></span>
                {slots.length > MIN_SLOTS && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto p-0 ml-auto text-muted-foreground hover:text-destructive hover:bg-transparent"
                    onClick={() => handleRemoveSlot(idx)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <ProductMultiSelect
                integrationId={integrationId}
                monthStartUtc={monthStartUtc}
                monthEndUtc={monthEndUtc}
                minimumOrderTotal={minimumOrderTotal}
                selected={slot}
                maxSelect={100}
                onSelectionChange={(products) => handleSlotChange(idx, products)}
                onSetNameChange={(name) => handleSlotLabelChange(idx, name)}
                placeholder={tUi("searchProductsForSlotValue0", { value0: String(idx + 1) })}
              />
            </div>
          ))}
        </div>

        {slots.length < MAX_SLOTS && (
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={handleAddSlot}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            <span><span key={tUi("addSlot")}>{tUi("addSlot")}</span></span>{slots.length}<span>/</span>{MAX_SLOTS}<span>)</span>
          </Button>
        )}
      </div>

      {/* Not enough filled slots */}
      {filledSlotCount < 2 && !isLoading && (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Search className="h-8 w-8 mb-2" />
          <p className="text-sm"><span key={tUi("fillAtLeast2SlotsWithProductsTo")}>{tUi("fillAtLeast2SlotsWithProductsTo")}</span></p>
        </div>
      )}

      {/* Loading */}
      {isLoading && filledSlotCount >= 2 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
          <KpiCardSkeleton />
        </div>
      )}

      {/* Bundle results */}
      {bundle && !isLoading && (
        <>
          {/* KPI Cards */}
          <div className={`grid grid-cols-1 md:grid-cols-2 ${bundle.bundle_groups.length > 2 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-4`}>
            <KpiCard
              label={tUi("fullBundleOrders")}
              icon={Package}
              formattedValue={
                bundle.full_bundle_order_ids?.length ? (
                  <OrdersLink
                    integrationId={integrationId}
                    monthStartUtc={monthStartUtc}
                    monthEndUtc={monthEndUtc}
                    orderIds={bundle.full_bundle_order_ids}
                  >
                    {bundle.full_bundle_orders.toLocaleString(uiFormattingLocale)}
                  </OrdersLink>
                ) : (
                  bundle.full_bundle_orders.toLocaleString(uiFormattingLocale)
                )
              }
              subtitle={tUi("ordersWithALLValue0Groups", { value0: String(bundle.bundle_groups.length) })}
              tooltip={tUi("numberOfOrdersThatContainAtLeastOne")}
            />
            <KpiCard
              label={tUi("fullBundleAvgOrderValue")}
              icon={TrendingUp}
              formattedValue={formatCurrency(bundle.full_bundle_aov)}
              subtitle={tUi("averageOrderValue")}
              tooltip={tUi("averageOrderValueAcrossOrdersThatContainThe")}
            />
            {bundle.bundle_groups.length > 2 && (
              <KpiCard
                label={tUi("partialOverlap")}
                icon={Users}
                formattedValue={bundle.partial_overlap_orders.toLocaleString(uiFormattingLocale)}
                subtitle={tUi("ordersWith2ButNotAll")}
                tooltip={tUi("ordersThatContainProductsFrom2OrMore")}
              />
            )}
            <KpiCard
              label={tUi("anyGroup")}
              icon={ShoppingCart}
              formattedValue={bundle.total_orders_with_any.toLocaleString(uiFormattingLocale)}
              subtitle={tUi("ordersWithAtLeast1")}
              tooltip={tUi("totalOrdersThatContainAtLeastOneProduct")}
            />
          </div>

          {/* Revenue card */}
          <KpiCard
            label={tUi("fullBundleRevenue")}
            icon={TrendingUp}
            formattedValue={formatCurrency(bundle.full_bundle_revenue)}
            subtitle={tUi("fromValue0FullBundleOrders", { value0: String(bundle.full_bundle_orders) })}
            tooltip={tUi("totalRevenueFromOrdersThatContainTheComplete")}
          />

          {/* Per-group breakdown chart */}
          {chartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">
                  <span><span key={tUi("inBundleVsWithoutBundle")}>{tUi("inBundleVsWithoutBundle")}</span></span><sup>
                    <TooltipProvider delayDuration={300}>
                      <RadixTooltip>
                        <TooltipTrigger asChild>
                          <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[280px]">
                          <p className="text-xs"><span key={tUi("inBundleOrdersWhereAllGroupsWerePurchased")}>{tUi("inBundleOrdersWhereAllGroupsWerePurchased")}</span></p>
                        </TooltipContent>
                      </RadixTooltip>
                    </TooltipProvider>
                  </sup>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 12 }} />
                    <RechartsTooltip
                      formatter={(value, name) => [value ?? 0, name]}
                    />
                    <Bar dataKey="in_bundle" name="In Bundle" stackId="orders" radius={[0, 0, 0, 0]}>
                      {chartData.map((entry, idx) => (
                        <Cell key={`bundle-${idx}`} fill={entry.color} fillOpacity={0.8} />
                      ))}
                    </Bar>
                    <Bar dataKey="without_bundle" name="Without Bundle" stackId="orders" radius={[0, 4, 4, 0]}>
                      {chartData.map((entry, idx) => (
                        <Cell key={`solo-${idx}`} fill={entry.color} fillOpacity={0.3} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Per-group stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium">
                <span><span key={tUi("groupStats")}>{tUi("groupStats")}</span></span><sup>
                  <TooltipProvider delayDuration={300}>
                    <RadixTooltip>
                      <TooltipTrigger asChild>
                        <Info className="inline h-2.5 w-2.5 cursor-help text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[250px]">
                        <p className="text-xs"><span key={tUi("breakdownOfEachGroupHowManyOrdersInclude")}>{tUi("breakdownOfEachGroupHowManyOrdersInclude")}</span></p>
                      </TooltipContent>
                    </RadixTooltip>
                  </TooltipProvider>
                </sup>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {bundle.group_stats.map((stat, idx) => {
                  const label = slotLabels[idx]
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-lg border"
                    >
                      <div
                        className="w-2 h-2 rounded-full mt-1.5 shrink-0"
                        style={{ backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">
                          {label ?? stat.title}
                        </p>
                        {label ? (
                          <p className="text-[11px] text-muted-foreground/70">
                            {stat.product_ids.length} <span key={stat.product_ids.length === 1 ? tUi("product") : tUi("products")}>{stat.product_ids.length === 1 ? tUi("product") : tUi("products")}</span>
                          </p>
                        ) : (
                          <>
                            {stat.sku && (
                              <p className="text-[11px] text-muted-foreground/70 truncate">{stat.sku}</p>
                            )}
                            {stat.product_ids.length > 1 && (
                              <p className="text-[11px] text-muted-foreground/70">
                                {stat.product_ids.length} <span><span key={tUi("productsInGroup")}>{tUi("productsInGroup")}</span></span>
                              </p>
                            )}
                          </>
                        )}
                        <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                          <p>
                            <span><span key={tUi("inBundle")}>{tUi("inBundle")}</span></span>
                            {bundle.full_bundle_order_ids?.length ? (
                              <OrdersLink
                                integrationId={integrationId}
                                monthStartUtc={monthStartUtc}
                                monthEndUtc={monthEndUtc}
                                orderIds={bundle.full_bundle_order_ids}
                                productIds={stat.product_ids}
                                className="text-muted-foreground"
                              >
                                {bundle.full_bundle_orders} <span><span key={tUi("orders")}>{tUi("orders")}</span></span>
                              </OrdersLink>
                            ) : (
                              <><TranslationText>{tUi.rich("value0Orders", { value0: String(bundle.full_bundle_orders) })}</TranslationText></>
                            )}
                          </p>
                          <p>
                            <span><span key={tUi("withoutBundle")}>{tUi("withoutBundle")}</span></span>
                            {(() => {
                              const withoutCount = stat.total_orders - bundle.full_bundle_orders
                              const fullSet = new Set(bundle.full_bundle_order_ids ?? [])
                              const withoutIds = stat.order_ids?.filter((id) => !fullSet.has(id)) ?? []
                              return withoutIds.length > 0 ? (
                                <OrdersLink
                                  integrationId={integrationId}
                                  monthStartUtc={monthStartUtc}
                                  monthEndUtc={monthEndUtc}
                                  orderIds={withoutIds}
                                  productIds={stat.product_ids}
                                  className="text-muted-foreground"
                                >
                                  {withoutCount} <span><span key={tUi("orders")}>{tUi("orders")}</span></span>
                                </OrdersLink>
                              ) : (
                                <><TranslationText>{tUi.rich("value0Orders", { value0: String(withoutCount) })}</TranslationText></>
                              )
                            })()}
                            {" "}<span>(</span>{formatCurrency(stat.solo_revenue)}<span>)</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Slots filled but no data (only show when not an error) */}
      {filledSlotCount >= 2 && !bundle && !isLoading && !error && (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Search className="h-8 w-8 mb-2" />
          <p className="text-sm"><span key={tUi("noOverlappingOrdersFoundForTheseProductGroups")}>{tUi("noOverlappingOrdersFoundForTheseProductGroups")}</span></p>
        </div>
      )}
    </div>
  )
}
